import type { MetricFormat } from './metrics';

/**
 * Promo Library runtime config, consumed via `useTpoGenConfig().promoLibraryConfig`.
 *
 * The Promo Library is a flat grid of promotion attributes + KPIs scoped by the
 * global filter — the TPO Gen replacement for the client-specific
 * `PromoLibraryContainer` under `organism/TradePromoOptimization`.
 *
 * This object is ASSEMBLED by `promoLibraryAdapter`, not authored:
 * - the backend `promo_library` section owns the grid (which columns exist, their
 *   order and labels, number formats, editability, page size, default sort);
 * - `fe_config.promoLibraryConfig` contributes presentation (`PromoLibraryUiConfig`:
 *   UI copy, overflow actions) plus the filter panel — `filterKeys` and their
 *   section labels are authored there like every other TPO Gen filter panel.
 *
 * It is `null` for a customer whose config has no `promo_library` section, or one
 * that sets `enabled: false` — the page then renders a not-configured notice
 * instead of an empty grid.
 *
 * `columns` is a single ORDERED list mixing both kinds:
 * - `kind: 'attribute'` — descriptor columns. Always rendered.
 * - `kind: 'kpi'` — measures. Toggled by the toolbar KPI picker and sent to the
 *   backend as the `kpis` request field, so hidden KPIs are never computed.
 */

/** Text/number alignment inside the cell and its header. */
export type PromoLibraryColumnAlign = 'left' | 'center' | 'right';

/** Cell formatting for `kind: 'kpi'` columns. */
export type PromoLibraryKpiFormat = 'number' | 'percent' | 'currency' | 'text';

interface PromoLibraryColumnBase {
    /**
     * The backend field name, and the column's identity everywhere else: the row
     * key in the response, the KPI picker, the URL state and `sort_by`.
     */
    key: string;
    label: string;
    width?: number;
    align?: PromoLibraryColumnAlign;
    /**
     * Pins the column while the rest of the grid scrolls horizontally. Tenants
     * with many KPIs pin their leading identity columns so the row stays
     * readable once it is wider than the viewport.
     */
    fixed?: 'left' | 'right';
    /**
     * Renders the column de-emphasised (muted header + shaded, greyed cells).
     * Used for reference columns that carry context but are not the focus.
     */
    muted?: boolean;
}

export interface PromoLibraryAttributeColumnConfig extends PromoLibraryColumnBase {
    kind: 'attribute';
    resolveLabelFrom?: string;
}

/**
 * Field type for the edit modal — mirrors the backend config's `data_type`,
 * extended with `dropdown` and `range` for specialised controls.
 * - `decimal`  – numeric input; the FE decides currency vs. percent rendering
 *                from `pre` / `post` (e.g. `$` prefix → currency, `%` suffix → percent).
 * - `integer`  – numeric input constrained to whole numbers.
 * - `text`     – free-text string input.
 * - `dropdown` – dropdown built from `allowedValues`.
 * - `boolean`  – toggle switch.
 * - `range`    – paired Min / Max numeric inputs.
 */
export type PromoLibraryFieldType =
    | 'decimal'
    | 'integer'
    | 'text'
    | 'dropdown'
    | 'boolean'
    | 'range';

/** Validation and editing metadata attached to an editable KPI column. */
export interface PromoLibraryEditConfig {
    /** Field type — determines the input control in the edit modal. */
    fieldType: PromoLibraryFieldType;
    /** Minimum accepted value (inclusive). `null` = unbounded. */
    minValue?: number | null;
    /** Maximum accepted value (inclusive). `null` = unbounded. */
    maxValue?: number | null;
    /** Whether negative numbers are accepted. Defaults to `false`. */
    allowNegative?: boolean;
    /** Maximum decimal places. `0` = integers only. `null` = unlimited. */
    allowedDecimals?: number | null;
    /** Fixed set of selectable values for `fieldType: 'dropdown'`. */
    allowedValues?: (string | number)[];
    /** Display prefix placed before the input (e.g. `"$"`). */
    prefix?: string;
    /** Display suffix placed after the input (e.g. `"%"`). */
    suffix?: string;
}

export interface PromoLibraryKpiColumnConfig extends PromoLibraryColumnBase {
    kind: 'kpi';
    format: PromoLibraryKpiFormat;
    /**
     * Display format for numeric KPIs — the configured `precision` / `prefix` /
     * `suffix`. Ignored when `format === 'text'`. Shares `MetricFormat` with the
     * promo grid and the reports so a value reads the same on every surface.
     */
    valueFormat: MetricFormat;
    /** Selected in the KPI picker on first load. Defaults to true. */
    defaultVisible?: boolean;
    /**
     * When present the KPI cell is clickable and opens the edit modal. Absent
     * or `undefined` means the column is read-only.
     */
    editConfig?: PromoLibraryEditConfig;
}

export type PromoLibraryColumnConfig = PromoLibraryAttributeColumnConfig | PromoLibraryKpiColumnConfig;

/** Filter attribute offered by the page's global filter. */
export interface PromoLibraryFilterKeyConfig {
    label: string;
    value: string;
    dimension: string;
    resolveLabelFrom?: string;
}

/** Dimension grouping shown as section headers inside the filter panel. */
export interface PromoLibraryFilterDimensionInfo {
    [dimension: string]: { label: string; key: string };
}

/**
 * Overflow (…) menu entry. Entries the FE has no handler for render disabled, so
 * a tenant can advertise a roadmap action without it looking broken.
 */
export interface PromoLibraryActionMenuItem {
    key: string;
    label: string;
}

/** Every user-facing string on the page. */
export interface PromoLibraryLabels {
    breadcrumb: string;
    sectionTitle: string;
    showChange: string;
    kpis: string;
    selectKpis: string;
    selectAll: string;
    deselectAll: string;
    noData: string;
    noDataSubtitle: string;
    /** `{loaded}` / `{total}` are substituted with the row counts. */
    recordCount: string;
}

/**
 * The `fe_config.promoLibraryConfig` slice: presentation only. Everything else is
 * derived from the backend `promo_library` section, so onboarding a tenant means
 * authoring UI copy here and the real config server-side.
 */
export interface PromoLibraryUiConfig {
    labels: PromoLibraryLabels;
    /**
     * Section headers for the filter panel, keyed by the dimension the backend
     * puts each attribute under. The backend sends dimension KEYS only, so their
     * display names live here.
     */
    filterDimensionInfo: PromoLibraryFilterDimensionInfo;
    filterKeys?: PromoLibraryFilterKeyConfig[];
    actionMenuItems: PromoLibraryActionMenuItem[];
}

export interface PromoLibraryConfig extends PromoLibraryUiConfig {
    /**
     * Backend `promo_library.library_groups[].key` for the library being shown.
     * Sent as the request's `library_group` so the backend knows which one to read.
     */
    libraryGroup: string;
    columns: PromoLibraryColumnConfig[];
    filterKeys: PromoLibraryFilterKeyConfig[];
    /** Shows the "Show Changes" switch. Off hides it and always renders current values. */
    showChangeToggle: boolean;
    /** Rows per page for the infinite-scroll grid. */
    pageSize: number;
    /** Initial `sort_by`. Falls back to the first sortable column. */
    defaultSortKey?: string;
}

/** Narrowing helper so callers can filter the mixed `columns` list. */
export const isPromoLibraryKpiColumn = (
    column: PromoLibraryColumnConfig,
): column is PromoLibraryKpiColumnConfig => column.kind === 'kpi';
